import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  doc,
  getDoc,
  addDoc,
  updateDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { CollegeAccount, Plan, AccountType, PLAN_LIMITS, PLAN_PRICING, normalizeAccount } from '../types';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

type FormState = Omit<CollegeAccount, 'id' | 'createdAt' | 'updatedAt'>;

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function oneYearFromStr(dateStr: string) {
  const d = new Date(dateStr);
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

const EMPTY_FORM: FormState = {
  name: '',
  type: 'college',
  city: '',
  state: '',
  website: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  domainEnabled: false,
  domain: '',
  csvEnabled: false,
  plan: 'starter',
  studentLimit: 500,
  startDate: todayStr(),
  expiryDate: oneYearFromStr(todayStr()),
  active: true,
  notes: '',
};

export function AccountFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const { user } = useAuth();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit && id) {
      loadAccount(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadAccount = async (accountId: string) => {
    const snap = await getDoc(doc(db, 'collegeAccounts', accountId));
    if (snap.exists()) {
      const account = normalizeAccount(snap.id, snap.data());
      setForm({
        name: account.name ?? '',
        type: account.type ?? 'college',
        city: account.city ?? '',
        state: account.state ?? '',
        website: account.website ?? '',
        contactName: account.contactName ?? '',
        contactEmail: account.contactEmail ?? '',
        contactPhone: account.contactPhone ?? '',
        domainEnabled: account.domainEnabled,
        domain: account.domain ?? '',
        csvEnabled: account.csvEnabled,
        plan: account.plan ?? 'starter',
        studentLimit: account.studentLimit ?? 500,
        startDate: account.startDate ?? '',
        expiryDate: account.expiryDate ?? '',
        active: account.active ?? true,
        notes: account.notes ?? '',
      });
    }
    setLoading(false);
  };

  const set = (field: keyof FormState, value: any) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'plan') {
        next.studentLimit = PLAN_LIMITS[value as Plan];
      }
      if (field === 'startDate' && value) {
        next.expiryDate = oneYearFromStr(value);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      console.log('[AccountForm] save attempt', {
        contextUserUid: user?.uid ?? null,
        firebaseAuthUid: auth.currentUser?.uid ?? null,
        isAdmin: user !== null,
        data: form,
      });

      const activationMethod = form.domainEnabled ? 'domain' : form.csvEnabled ? 'csv' : null;
      const payload = {
        ...form,
        ...(activationMethod ? { activationMethod } : {}),
      };

      if (isEdit && id) {
        await updateDoc(doc(db, 'collegeAccounts', id), {
          ...payload,
          updatedAt: serverTimestamp(),
        });
        navigate(`/accounts/${id}`);
      } else {
        const ref = await addDoc(collection(db, 'collegeAccounts'), {
          ...payload,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        navigate(`/accounts/${ref.id}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save account.');
      setSaving(false);
    }
  };

  const backTo = isEdit ? `/accounts/${id}` : '/dashboard';

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading...</div>
      </Layout>
    );
  }

  const pricing = PLAN_PRICING[form.type];

  return (
    <Layout>
      <div style={{ maxWidth: 720 }}>
        <button className="back-btn" onClick={() => navigate(backTo)}>
          ← Back
        </button>
        <h1 className="page-title" style={{ marginBottom: 24 }}>
          {isEdit ? 'Edit College' : 'Add New College'}
        </h1>

        {error && <div className="error-message" style={{ marginBottom: 20 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Account Info */}
          <Section title="College Info">
            <Grid>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">College Name *</label>
                <input
                  className="form-input"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="e.g. Virginia Tech"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Type *</label>
                <select
                  className="form-input"
                  value={form.type}
                  onChange={(e) => set('type', e.target.value as AccountType)}
                >
                  <option value="college">College</option>
                  <option value="alumni">Alumni Association</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Website</label>
                <input
                  className="form-input"
                  value={form.website}
                  onChange={(e) => set('website', e.target.value)}
                  placeholder="https://"
                />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  className="form-input"
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">State</label>
                <input
                  className="form-input"
                  value={form.state}
                  onChange={(e) => set('state', e.target.value)}
                  placeholder="e.g. VA"
                />
              </div>
            </Grid>
          </Section>

          {/* Contact */}
          <Section title="Contact">
            <Grid>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label">Contact Name</label>
                <input
                  className="form-input"
                  value={form.contactName}
                  onChange={(e) => set('contactName', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  value={form.contactEmail}
                  onChange={(e) => set('contactEmail', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  className="form-input"
                  value={form.contactPhone}
                  onChange={(e) => set('contactPhone', e.target.value)}
                />
              </div>
            </Grid>
          </Section>

          {/* Activation */}
          <Section title="Activation">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.domainEnabled}
                    onChange={(e) => set('domainEnabled', e.target.checked)}
                    style={{ marginTop: 2, accentColor: 'var(--accent)', width: 16, height: 16, flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: 14 }}>
                      Domain activation
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                      Grants access to anyone signing in with a matching email domain
                    </div>
                  </div>
                </label>
                {form.domainEnabled && (
                  <div className="form-group" style={{ marginTop: 12, paddingLeft: 26 }}>
                    <label className="form-label">Domain</label>
                    <input
                      className="form-input"
                      value={form.domain ?? ''}
                      onChange={(e) => set('domain', e.target.value)}
                      placeholder="e.g. vt.edu"
                      style={{ maxWidth: 300 }}
                    />
                  </div>
                )}
              </div>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.csvEnabled}
                  onChange={(e) => set('csvEnabled', e.target.checked)}
                  style={{ marginTop: 2, accentColor: 'var(--accent)', width: 16, height: 16, flexShrink: 0 }}
                />
                <div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: 14 }}>
                    CSV upload
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                    Manually whitelist specific email addresses via CSV file
                  </div>
                </div>
              </label>
            </div>
          </Section>

          {/* Plan */}
          <Section title="Plan & Pricing">
            <Grid>
              <div className="form-group">
                <label className="form-label">Plan *</label>
                <select
                  className="form-input"
                  value={form.plan}
                  onChange={(e) => set('plan', e.target.value as Plan)}
                >
                  {(['starter', 'growth', 'campus', 'university'] as Plan[]).map((p) => (
                    <option key={p} value={p}>
                      {p.charAt(0).toUpperCase() + p.slice(1)} — {pricing[p]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Student / Member Limit</label>
                <input
                  className="form-input"
                  type="number"
                  min={1}
                  value={form.studentLimit}
                  onChange={(e) => set('studentLimit', parseInt(e.target.value) || 0)}
                />
              </div>
            </Grid>
            <div
              style={{
                marginTop: 12,
                padding: '10px 14px',
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-md)',
                fontSize: 12,
                color: 'var(--text-secondary)',
                lineHeight: 1.8,
              }}
            >
              <strong style={{ color: 'var(--text-primary)' }}>
                {form.type === 'college' ? 'College' : 'Alumni Association'} pricing:
              </strong>{' '}
              {(['starter', 'growth', 'campus', 'university'] as Plan[]).map((p, i) => (
                <span key={p}>
                  {i > 0 && <span style={{ color: 'var(--border)', margin: '0 6px' }}>·</span>}
                  <span style={{ color: form.plan === p ? 'var(--accent-text)' : undefined }}>
                    {p.charAt(0).toUpperCase() + p.slice(1)} {pricing[p]}
                  </span>
                </span>
              ))}
            </div>
          </Section>

          {/* Dates & Status */}
          <Section title="Dates & Status">
            <Grid>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input
                  className="form-input"
                  type="date"
                  value={form.startDate}
                  onChange={(e) => set('startDate', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Expiry Date</label>
                <input
                  className="form-input"
                  type="date"
                  value={form.expiryDate}
                  onChange={(e) => set('expiryDate', e.target.value)}
                />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="form-label" style={{ marginBottom: 10 }}>
                  Status
                </label>
                <label className="toggle-switch" onClick={() => set('active', !form.active)}>
                  <div className={`toggle-track ${form.active ? 'on' : ''}`}>
                    <div className="toggle-thumb" />
                  </div>
                  <span
                    style={{
                      color: form.active ? 'var(--accent-text)' : 'var(--text-secondary)',
                      fontSize: 14,
                    }}
                  >
                    {form.active ? 'Active' : 'Inactive'}
                  </span>
                </label>
              </div>
            </Grid>
          </Section>

          {/* Notes */}
          <Section title="Internal Notes">
            <div className="form-group">
              <textarea
                className="form-input"
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                rows={4}
                placeholder="Internal notes visible only to admins..."
              />
            </div>
          </Section>

          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create College'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(backTo)}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: 16,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {children}
    </div>
  );
}
